import * as React from 'react';
import { useAppSelector } from 'hooks';
import { selectCurrentCampaignId } from 'store/campaigns';
import { selectCurrentClientId } from 'store/clients';
import { selectCurrentProposalId } from 'store/proposals';
import { selectCurrentAdSlotId } from 'store/adslots';
import { selectCurrentAdFormatId } from 'store/adformats';
import { selectCurrentCommittedProposalId } from 'store/proposals/committed';
import { ReactComponent } from 'types';

const SELECTORS: Record<string, any> = {
  campaign: selectCurrentCampaignId,
  client: selectCurrentClientId,
  proposal: selectCurrentProposalId,
  adSlot: selectCurrentAdSlotId,
  adFormat: selectCurrentAdFormatId,
  committedProposal: selectCurrentCommittedProposalId,
};

export const withCurrentEntityId = (
  Component: React.ElementType,
  entityName:
    | 'campaign'
    | 'client'
    | 'proposal'
    | 'adSlot'
    | 'adFormat'
    | 'committedProposal'
    | 'counterOfferProposal'
    | 'clientManager',
): React.ElementType => {
  const ComponentWithCurrentEntityId = ({
    children,
    ...props
  }: ReactComponent): JSX.Element | null => {
    const entitySelector = SELECTORS[entityName];
    const entityId = useAppSelector(entitySelector);
    if (!entityId) return null;

    return (
      <Component {...props} {...{ [`${entityName}Id`]: entityId }}>
        {children}
      </Component>
    );
  };
  return ComponentWithCurrentEntityId;
};

export const withCurrentCampaignId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'campaign');

export const withCurrentClientId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'client');

export const withCurrentProposalId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'proposal');

export const withCurrentCommittedProposalId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'committedProposal');

export const withCurrentAdSlotId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'adSlot');

export const withCurrentAdFormatId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'adFormat');

export const withCurrentClientManagerId = (
  Component: React.ElementType,
): React.ElementType => withCurrentEntityId(Component, 'clientManager');
